"use client";
import GridIcon from "@/app/assets/images/svg/grid.svg";
import ListIcon from "@/app/assets/images/svg/list.svg";
import ProductListItem from "@/components/_Search/UI/ProductListItem/ProductListItem";
import IconOnlyButton from "@/components/UI/Button/IconOnlyButton/IconOnlyButton";
import { useI18n } from "@/i18n/client";
import { Category, ProductData } from "@/lib/5874/types";
import { SearchMeta, SearchTracking } from "@/lib/_search-providers/types";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import ProductCard from "../../../ProductCard/ProductCard";
import FilterAndSort from "../SortAndFilter/FilterAndSort";
import styles from "./ToggleView.module.scss";

import { useAnalyticsContext } from "@/lib/_analytics/AnalyticsContextProvider";
import { ProductsViewedTrigger } from "@/lib/_event-bus/components/ProductsViewedTrigger";
import { QueryIdSetter } from "@/lib/_event-bus/components/QueryIdSetter";
import { useSearchContext } from "../../provider/SearchContextProvider";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { getEnrichedProducts } from "@/lib/_search-providers/algolia/actions";

export type ViewStyle = "grid" | "list";

interface ViewProps {
	results: ProductData[] | undefined;
	category?: Category;
	currencyCode: string;
	startingPosition: number;
	viewStyle: ViewStyle;
	queryId?: string;
}

type ToggleProps = {
	itemCount: SearchMeta["count"];
	results: ProductData[];
	category?: Category;
	tracking: SearchTracking;
};

export const ToggleView = ({
	itemCount,
	results,
	category,
	tracking,
}: ToggleProps) => {
	const { facets, count } = useSearchContext();
	const { insightsClient } = useAnalyticsContext();

	const [view, setView] = useState<ViewStyle>("grid");
	const t = useI18n();
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);

	useEffect(() => {
		insightsClient.viewFilters(facets.options);
	}, [facets]);

	const handleChangeView = (selectedView: ViewStyle) => {
		setView(selectedView);
	};

	return (
		<>
			<QueryIdSetter
				queryID={tracking.queryId}
				pageType='search'
			/>
			<ProductsViewedTrigger
				products={results}
				eventName='Search Result Products Viewed'
			/>
			<div className={styles.filters}>
				<FilterAndSort />

				<span className={styles.count}>
					{t("PLP.itemCount", {
						count: itemCount.total,
						from: itemCount.from,
						to: itemCount.to,
					} as any)}
				</span>
				{results.length > 0 && (
					<div className={styles.view}>
						<IconOnlyButton
							variant='secondary'
							onClick={() => handleChangeView("grid")}
							className={clsx([
								styles.iconOnlyButton,
								view === "grid" ? styles.active : null,
							])}
							icon={<GridIcon />}
						/>
						<IconOnlyButton
							variant='secondary'
							onClick={() => handleChangeView("list")}
							className={clsx([
								styles.iconOnlyButton,
								view === "list" ? styles.active : null,
							])}
							icon={<ListIcon />}
						/>
					</div>
				)}
			</div>
			<View
				results={results}
				category={category}
				currencyCode={currencyFormattingRules.currency}
				startingPosition={count.from}
				viewStyle={view}
				queryId={tracking.queryId}
			/>
		</>
	);
};

const View = ({
	category,
	currencyCode,
	startingPosition,
	viewStyle,
	queryId,
	results = [],
}: ViewProps) => {
	const viewClassName = clsx([
		viewStyle === "grid" ? styles.gridView : styles.listView,
	]);

	return (
		<div className={viewClassName}>
			{results.map((product, index) => {
				// cast as any to add additional props
				const propsToPass = {
					product,
					category,
					currencyCode,
					"data-position": index + startingPosition,
					"data-queryid": queryId,
				} as any;

				const Component = viewStyle === "list" ? ProductListItem : ProductCard;

				if (viewStyle === "grid") {
					// Used to determine if a ProductCard is above the fold
					const PRODUCTS_PER_ROW = 4;
					const VISIBLE_ROWS = 2;
					const isAboveTheFold = index < PRODUCTS_PER_ROW * VISIBLE_ROWS;

					propsToPass.isAboveTheFold = isAboveTheFold;
				}

				return (
					<Component
						key={product.id}
						{...propsToPass}
					/>
				);
			})}
		</div>
	);
};
